import type { BoardroomContacts, BoardroomContactsRow } from '../../../types';
import DataTable from './DataTable';
import { shortUrl } from './EmailCaptureReport';
import type { Column } from './tableColumns';

/**
 * Contacts: a row per company, how to reach it. Read off the pages already fetched for each
 * company, so it fills in as their sites are read; nothing here starts a fetch.
 */

type Row = BoardroomContactsRow;

const NETWORK_NAMES: Record<string, string> = {
  linkedin: 'LinkedIn',
  x: 'X',
  facebook: 'Facebook',
  instagram: 'Instagram',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  pinterest: 'Pinterest',
  github: 'GitHub',
  threads: 'Threads',
  bluesky: 'Bluesky',
};

/** A social network's short name. */
export function networkName(key: string): string {
  return NETWORK_NAMES[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
}

/** What the contact form asks for, e.g. "name*, email*, message". A star marks a required field. */
export function formText(row: Row): string {
  return (row.contact_form?.fields ?? [])
    .map(f => (f.name || f.type) + (f.required ? '*' : ''))
    .filter(Boolean)
    .join(', ');
}

/** The count line above the table. */
export function countText(data: BoardroomContacts): string {
  return (
    `Contact details for ${data.found} of ${data.companies} companies. ` +
    `${data.with_email} show an email, ${data.with_phone} a phone, ${data.with_chat} a chat tool.`
  );
}

export default function ContactsReport({
  data,
  onOpen,
  roomName,
}: {
  data: BoardroomContacts;
  onOpen: (itemId: number) => void;
  roomName?: string;
}) {
  const LINK = 'text-indigo-600 dark:text-indigo-400 hover:underline text-left';
  const rows = data.rows.filter(r => r.status === 'found');
  const columns: Column<Row>[] = [
    {
      key: 'title',
      label: 'Company',
      width: 12,
      always: true,
      value: r => r.title,
      cell: r => (
        <button type="button" onClick={() => onOpen(r.id)} className={LINK}>
          {r.title}
        </button>
      ),
    },
    {
      key: 'emails',
      label: 'Emails',
      width: 14,
      value: r => r.emails.join(' '),
      cell: r => (
        <ul className="space-y-0.5">
          {r.emails.map(e => (
            <li key={e}>
              <a href={`mailto:${e}`} className={LINK}>
                {e}
              </a>
            </li>
          ))}
        </ul>
      ),
    },
    {
      key: 'phones',
      label: 'Phones',
      width: 10,
      value: r => r.phones.join(' '),
      cell: r => (
        <ul className="space-y-0.5">
          {r.phones.map(p => (
            <li key={p}>
              <a href={`tel:${p.replace(/[^\d+]/g, '')}`} className={LINK}>
                {p}
              </a>
            </li>
          ))}
        </ul>
      ),
    },
    {
      key: 'contact_pages',
      label: 'Contact page',
      width: 12,
      value: r => r.contact_pages.join(' '),
      cell: r => (
        <ul className="space-y-0.5">
          {r.contact_pages.map(u => (
            <li key={u}>
              <a href={u} target="_blank" rel="noopener noreferrer" className={LINK}>
                {shortUrl(u)}
              </a>
            </li>
          ))}
        </ul>
      ),
    },
    {
      key: 'socials',
      label: 'Socials',
      width: 10,
      sortable: false,
      value: r => r.socials.map(s => s.url).join(' '),
      cell: r => (
        <span className="flex flex-wrap gap-x-2">
          {r.socials.map(s => (
            <a key={s.network} href={s.url} target="_blank" rel="noopener noreferrer" className={LINK}>
              {networkName(s.network)}
            </a>
          ))}
        </span>
      ),
    },
    { key: 'chat', label: 'Chat tool', width: 8, value: r => r.chat.join(', ') },
    { key: 'address', label: 'Address', width: 12, value: r => r.address },
    {
      key: 'form',
      label: 'Contact form asks for',
      width: 12,
      value: r => formText(r),
      cell: r =>
        r.contact_form ? (
          <div className="space-y-0.5">
            <div>{formText(r) || 'No fields read'}</div>
            <a href={r.contact_form.url} target="_blank" rel="noopener noreferrer" className={LINK}>
              {shortUrl(r.contact_form.url)}
            </a>
          </div>
        ) : null,
    },
  ];
  return (
    <div className="min-w-0 space-y-2 text-xs" data-testid="contacts">
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {countText(data)} Nothing found for {data.nothing}, and {data.unread} have not had a page read yet. Only pages
        fetched from now on are read, so this fills in as their sites are read. A star marks a required form field.
      </p>
      {data.chat_tools.length > 0 && (
        <p className="text-gray-500 dark:text-gray-400">
          {data.chat_tools
            .slice(0, 6)
            .map(t => `${t.name} ${t.companies}`)
            .join(' · ')}
        </p>
      )}
      <DataTable
        table="contacts"
        room={roomName}
        file="contacts"
        columns={columns}
        rows={rows}
        rowKey={r => `contacts-row-${r.id}`}
        empty="No contact details found on the pages read so far."
      />
    </div>
  );
}
